/**
 * AmbienteCard — card de ambiente da Produção v2.
 *
 * Porte fiel do componente canônico aprovado pela gestora
 * ("Componente - Card de Ambiente (PharuERP).html"). Um card, duas densidades
 * (prop `densidade`): "full" (galeria/grids largos) e "compact" (colunas
 * estreitas do board — esconde a faixa de contexto, o nome do responsável e
 * reduz o rodapé a 2 métricas).
 *
 * Anatomia em 4 faixas (brief 0.9):
 *   1. Contexto  — "PD-2841 · Cliente" uppercase neutro; à direita SÓ o selo
 *      de exceção (Congelado = warning / Atrasado = danger).
 *   2. Título    — nome do AMBIENTE, peso 500 (não bold), clamp 2 linhas
 *      + title.
 *   3. Pessoa    — ícone pessoas + UserAvatar + responsável + pills soft
 *      (fase accent · etapa outline neutra · lote outline).
 *   4. Rodapé    — nº peças · ANEL DE PROGRESSO (donut SVG) · atraso
 *      (danger só se > 0, senão "–") · bolinha do buffer na cor do fever.
 *
 * REGRA DE BORDA: neutra por padrão; borda colorida SÓ como exceção de
 * estado (warning = congelado, danger = atrasado). Fase NÃO é estado — vai
 * na pill, nunca na borda.
 *
 * Mapa de cores mockup -> tokens do tema (dark mode automático):
 *   #FFFFFF surface · #E6E8EF border · #1A2347 ink · #8A92A6 ink-3/ink-4
 *   #5B5BF6 accent · rgba(91,91,246,.12) accent-soft · #E7B765 warning-border
 *   #EBA3A3 danger-border · #F0F1F6 border-subtle · #16A34A/#D97706/#DC2626
 *   success/warning/danger-solid.
 *
 * @param {object}  props
 * @param {string}  props.codigo        Código do ambiente (AMB-UN1-540).
 * @param {string}  props.nome          Nome do ambiente ("Cozinha — Ilha central").
 * @param {string}  props.pedidoNumero  Código do pedido (PD-UN1-2841).
 * @param {string}  props.cliente       Nome do cliente.
 * @param {{nome: string, avatar?: string}} [props.responsavel] Responsável da etapa.
 * @param {string}  [props.faseLabel]   Rótulo da fase (pill accent, ex.: "F6 Marcenaria").
 * @param {string}  [props.etapaLabel]  Etapa atual (pill outline, ex.: "Caixaria").
 * @param {string}  [props.loteNumero]  Lote quando relevante (ex.: "LT-118").
 * @param {Function} [props.onLoteClick] Torna o chip do lote clicável
 *   (abre o detalhe do lote — Lote C; stopPropagation p/ não abrir o card).
 * @param {boolean} [props.semLote]     Selo warning "Sem lote" (Lote C §2.4:
 *   ambiente avançou por F4/F5 sem lote — anomalia visível, nada some).
 * @param {number}  [props.pecasTotal]  Nº de peças do ambiente.
 * @param {number}  [props.progressoPct] % de etapas concluídas (anel).
 * @param {number}  [props.atrasoDias]  Dias de atraso (0 = sem atraso, mostra "–").
 * @param {number}  [props.bufferPct]   Consumo de buffer em %.
 * @param {"verde"|"amarelo"|"vermelho"} [props.bufferZona] Zona do fever chart.
 * @param {boolean} [props.congelado]   Selo/borda de congelamento (warning).
 * @param {"full"|"compact"} [props.densidade] Densidade do card.
 * @param {boolean} [props.mostrarContexto] Força a faixa 1 (PD · cliente)
 *   também na densidade compact — brief 0.9: compact esconde o contexto
 *   apenas "quando o pedido já está no título"; em grids multi-cliente
 *   (ex.: pool da Otimização) o contexto volta a ser necessário.
 * @param {Function} [props.onClick]    Card inteiro clicável (alvo de luva).
 */
import React from "react";
import { FiBox, FiClock, FiCloudSnow, FiUsers } from "react-icons/fi";
import styled, { css } from "styled-components";

import UserAvatar from "../UserAvatar";
import AnelProgresso from "./AnelProgresso";

const COR_ZONA_BUFFER = {
  verde: "var(--success-solid)",
  amarelo: "var(--warning-solid)",
  vermelho: "var(--danger-solid)",
};

const Cartao = styled.article`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: var(--shadow-1);
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  min-width: 0;

  ${({ $excecao }) =>
    $excecao === "warn" &&
    css`
      border-color: var(--warning-border);
      box-shadow: 0 1px 2px rgba(217, 119, 6, 0.12);
    `}
  ${({ $excecao }) =>
    $excecao === "danger" &&
    css`
      border-color: var(--danger-border);
    `}

  ${({ $compact }) =>
    $compact &&
    css`
      padding: 11px 12px;
      gap: 7px;
      border-radius: 12px;
    `}

  ${({ $clicavel }) =>
    $clicavel &&
    css`
      cursor: pointer;
      &:hover {
        border-color: var(--border-strong);
        box-shadow: var(--shadow-2);
      }
      &:focus-visible {
        outline: none;
        box-shadow: var(--shadow-focus);
      }
    `}
`;

const Contexto = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--ink-3);
  font-weight: 500;
  min-width: 0;

  > span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  font-size: 10px;
  text-transform: none;
  letter-spacing: 0;
  padding: 2px 7px;
  border-radius: var(--radius-pill, 999px);
  font-weight: 500;
  background: ${({ $tone }) =>
    $tone === "danger" ? "var(--danger-bg)" : "var(--warning-bg)"};
  color: ${({ $tone }) =>
    $tone === "danger" ? "var(--danger-text)" : "var(--warning-text)"};
`;

const Titulo = styled.div`
  font-size: ${({ $compact }) => ($compact ? "13.5px" : "15px")};
  font-weight: 500;
  color: var(--ink);
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Pessoa = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;

  .pic {
    color: var(--ink-4);
    display: flex;
  }
`;

/* UserAvatar do repo tem presets fixos (xs = 24px); o card aprovado pede
   22px (full) e 18px (compact). O wrapper força a dimensão exata por CSS
   (especificidade classe+elemento vence a classe do AvatarWrapper). */
const AvatarMini = styled.span`
  display: inline-flex;
  flex-shrink: 0;

  & > span {
    width: ${({ $px }) => $px}px;
    height: ${({ $px }) => $px}px;
    font-size: ${({ $px }) => ($px >= 20 ? 10 : 9)}px;
  }
`;

const NomeResponsavel = styled.span`
  font-size: 12px;
  color: var(--ink-2);
  margin-right: 2px;
`;

const Pill = styled.span`
  font-size: 10.5px;
  padding: 2px 8px;
  border-radius: var(--radius-pill, 999px);
  white-space: nowrap;

  ${({ $tipo }) =>
    $tipo === "fase"
      ? css`
          background: var(--accent-soft);
          color: var(--accent);
          font-weight: 500;
        `
      : css`
          border: 1px solid var(--border-strong);
          color: var(--ink-3);
        `}
`;

/* Chip do lote clicável (Lote C): mesmo desenho do Pill outline, mas como
   <button> real — hover accent + focus ring; stopPropagation no handler. */
const PillLoteBotao = styled.button`
  font-family: inherit;
  font-size: 10.5px;
  padding: 2px 8px;
  border-radius: var(--radius-pill, 999px);
  white-space: nowrap;
  border: 1px solid var(--border-strong);
  color: var(--ink-3);
  background: transparent;
  cursor: pointer;

  &:hover {
    color: var(--accent);
    border-color: var(--accent);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const Rodape = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ $compact }) => ($compact ? "14px" : "16px")};
  font-size: 12px;
  color: var(--ink-3);
  padding-top: 2px;
  border-top: 1px solid var(--border-subtle);
`;

const Metrica = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  color: ${({ $alerta }) => ($alerta ? "var(--danger-text)" : "inherit")};

  svg {
    font-size: 14px;
    color: ${({ $alerta }) => ($alerta ? "var(--danger-text)" : "var(--ink-4)")};
  }
`;

const Mono = styled.span`
  font-variant-numeric: tabular-nums;
`;

const DotBuffer = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  background: ${({ $zona }) => COR_ZONA_BUFFER[$zona] || COR_ZONA_BUFFER.verde};
`;

function AmbienteCard({
  codigo,
  nome,
  pedidoNumero,
  cliente,
  responsavel,
  faseLabel,
  etapaLabel,
  loteNumero,
  onLoteClick,
  semLote = false,
  pecasTotal,
  progressoPct = 0,
  atrasoDias = 0,
  bufferPct = 0,
  bufferZona = "verde",
  congelado = false,
  densidade = "full",
  mostrarContexto,
  onClick,
}) {
  const compact = densidade === "compact";
  // Faixa 1 (contexto): sempre no full; no compact só quando pedido/opt-in.
  const exibirContexto = !compact || Boolean(mostrarContexto);
  // Congelado tem precedência sobre atraso (é o estado acionável).
  const excecao = congelado ? "warn" : atrasoDias > 0 ? "danger" : null;
  const clicavel = typeof onClick === "function";
  const atrasado = atrasoDias > 0;

  return (
    <Cartao
      $compact={compact}
      $excecao={excecao}
      $clicavel={clicavel}
      onClick={onClick}
      role={clicavel ? "button" : undefined}
      tabIndex={clicavel ? 0 : undefined}
      onKeyDown={
        clicavel
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      aria-label={clicavel ? `Abrir ambiente ${codigo || nome}` : undefined}
    >
      {exibirContexto && (
        <Contexto>
          <span>
            {pedidoNumero}
            {cliente ? ` · ${cliente}` : ""}
          </span>
          {excecao === "warn" && (
            <Chip $tone="warn">
              <FiCloudSnow size={11} aria-hidden="true" />
              Congelado
            </Chip>
          )}
          {excecao === "danger" && (
            <Chip $tone="danger">
              <FiClock size={11} aria-hidden="true" />
              Atrasado
            </Chip>
          )}
        </Contexto>
      )}

      <Titulo $compact={compact} title={nome}>
        {nome}
      </Titulo>

      <Pessoa>
        {!compact && (
          <span className="pic" aria-hidden="true">
            <FiUsers size={14} />
          </span>
        )}
        {/* Sem responsável (o board do P1 ainda não expõe o dado — contrato
            RH 0.8 chega depois), o avatar viraria um "?" sem sentido: só
            renderiza quando há nome. */}
        {responsavel?.nome && (
          <AvatarMini $px={compact ? 18 : 22}>
            <UserAvatar
              nome={responsavel.nome}
              src={responsavel.avatar}
              size="xs"
            />
          </AvatarMini>
        )}
        {!compact && responsavel?.nome && (
          <NomeResponsavel>{responsavel.nome}</NomeResponsavel>
        )}
        {faseLabel && <Pill $tipo="fase">{faseLabel}</Pill>}
        {etapaLabel && <Pill $tipo="etapa">{etapaLabel}</Pill>}
        {loteNumero
          && (typeof onLoteClick === "function" ? (
            <PillLoteBotao
              type="button"
              title={`Produzido no lote ${loteNumero} — abrir detalhe`}
              aria-label={`Abrir lote ${loteNumero}`}
              onClick={(e) => {
                e.stopPropagation();
                onLoteClick(e);
              }}
            >
              {loteNumero}
            </PillLoteBotao>
          ) : (
            <Pill $tipo="lote" title={`Produzido no lote ${loteNumero}`}>
              {loteNumero}
            </Pill>
          ))}
        {semLote && (
          <Chip title="Ambiente avançou sem lote — agrupe na Otimização">
            Sem lote
          </Chip>
        )}
      </Pessoa>

      <Rodape $compact={compact}>
        {!compact && (
          <Metrica title={`${pecasTotal ?? 0} peças`}>
            <FiBox aria-hidden="true" />
            <Mono>{pecasTotal ?? 0}</Mono>
          </Metrica>
        )}
        <Metrica>
          <AnelProgresso
            pct={progressoPct}
            size={compact ? 26 : 30}
            stroke={compact ? 3.4 : 3.2}
            titulo={`${Math.round(progressoPct)}% das etapas concluídas`}
          />
        </Metrica>
        {!compact && (
          <Metrica
            $alerta={atrasado}
            title={atrasado ? `${atrasoDias} dia(s) de atraso` : "Sem atraso"}
          >
            <FiClock aria-hidden="true" />
            <Mono>{atrasado ? `+${atrasoDias}d` : "–"}</Mono>
          </Metrica>
        )}
        <Metrica title={`Consumo de buffer: ${bufferPct}% (zona ${bufferZona})`}>
          <DotBuffer $zona={bufferZona} aria-hidden="true" />
          <Mono>{compact ? `${bufferPct}%` : `buf ${bufferPct}%`}</Mono>
        </Metrica>
      </Rodape>
    </Cartao>
  );
}

export default AmbienteCard;
